// @ts-nocheck
import React, { useMemo, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Col, FormGroup, Label, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { DatePicker, Input } from "antd";
import dayjs from "dayjs";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useIncidentReportLookupsQuery } from "@/services/reports";
import { useSystem } from "@/utils/hooks/useLocation";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import DocumentField from "./DocumentField";

const { TextArea } = Input;

const toSelectOptions = (items, getLocalizedText) =>
  (items || []).map((item) => ({
    value: item.id != null ? Number(item.id) : item.id,
    label: getLocalizedText(item?.name_ar, item?.name_en) ?? item?.name_en ?? item?.name_ar ?? "",
    system: item?.system ?? null,
  }));

/** Get array of items from lookup (supports { items: [] }, { data: [] }, or raw array) */
const getLookupItems = (obj) =>
  obj?.items ?? obj?.data ?? (Array.isArray(obj) ? obj : []);




function CaseDetails({
  control,
  FormField,
  watch,
  setValue,
  threeCol,
  fullWidthCol,
  healthCertificateInputRef,
  existingDocuments = [],
  reportId = null,
}) {
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  let system = currentSystem === "makkah" ? "makkah" : "madina";
  const { getLocalizedText } = useLocalizedText();
  const { data: lookupsData } = useIncidentReportLookupsQuery({ system: currentSystem });
  const lookups = lookupsData?.data?.data ?? lookupsData?.data ?? {};

  const casesTypes = useMemo(
    () => toSelectOptions(getLookupItems(lookups.case_types), getLocalizedText),
    [lookups.case_types, getLocalizedText]
  );
  // const caseStatusesOptions = useMemo(
  //   () => toSelectOptions(getLookupItems(lookups.case_statuses), getLocalizedText),
  //   [lookups.case_statuses, getLocalizedText]
  // );
  const caseAuthorityOptions = useMemo(
    () => toSelectOptions(getLookupItems(lookups.case_authorities), getLocalizedText),
    [lookups.case_authorities, getLocalizedText]
  );
  const cardStatusOptions = useMemo(
    () => toSelectOptions(getLookupItems(lookups.card_statuses), getLocalizedText),
    [lookups.card_statuses, getLocalizedText]
  );
  // const caseStatusesOptions = useMemo(
  //   () => toSelectOptions(getLookupItems(lookups.case_statuses), getLocalizedText),
  //   [lookups.case_statuses, getLocalizedText]
  // );
  const documentTypeOptions = useMemo(
    () => toSelectOptions(getLookupItems(lookups.document_types), getLocalizedText),
    [lookups.document_types, getLocalizedText]
  );
  const reportAuthorityOptions = useMemo(
    () => toSelectOptions(getLookupItems(lookups.report_authorities), getLocalizedText),
    [lookups.report_authorities, getLocalizedText]
  );
  const bodyLocationOptions = useMemo(
    () => toSelectOptions(getLookupItems(lookups.body_locations), getLocalizedText),
    [lookups.body_locations, getLocalizedText]
  );

  const caseTypeId = watch?.("case_type_id");
  const caseTypeItems = getLookupItems(lookups.case_types);
  const selectedCaseType = useMemo(
    () => caseTypeItems?.find((i) => String(i.id) === String(caseTypeId)) ?? null,
    [caseTypeItems, caseTypeId]
  );
  const caseTypeActionsText = useMemo(() => {
    let actions =
      selectedCaseType?.actions ??
      selectedCaseType?.action ??
      selectedCaseType?.case_type_actions ??
      [];
    if (!Array.isArray(actions) && selectedCaseType && caseTypeId != null) {
      const byId =
        lookups.case_type_actions?.[String(caseTypeId)] ??
        lookups.actions?.[String(caseTypeId)];
      actions = Array.isArray(byId) ? byId : [];
    }
    if (!Array.isArray(actions) || actions.length === 0) return "";
    const sorted = [...actions].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    return sorted
      .map((action, idx) => `${idx + 1}. ${getLocalizedText(action?.name_ar, action?.name_en) ?? action?.name_en ?? action?.name_ar ?? ""}`)
      .join("\n");
  }, [selectedCaseType, caseTypeId, lookups.case_type_actions, lookups.actions, getLocalizedText]);
  useEffect(() => {
    if (!setValue) return;
    if (!caseTypeId) {
      setValue("action_taken", "", { shouldDirty: false });
      return;
    }
    if (selectedCaseType) {
      setValue("action_taken", caseTypeActionsText, { shouldDirty: false });
    }
  }, [caseTypeId, selectedCaseType, caseTypeActionsText, setValue]);

  const isDeathCase = useMemo(() => {
    return Boolean(selectedCaseType && String(selectedCaseType.code).toUpperCase() === "DEATH");
  }, [selectedCaseType]);

  return (
    <>
      {/* 75% width on md and lg only */}
      <Row>
        <Col xs={12} md={9} lg={9}>
          <Row className="g-2 mb-2">
            <FormField name="case_type_id" label={t("CASE_TYPES")} required colProps={{ xs: 12 }} errorInside>
              <Controller
                name="case_type_id"
                control={control}
                rules={{ required: t("FIELD_REQUIRED") }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <>
                    <AntSelect
                      {...field}
                      status={invalid ? "error" : undefined}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={t("ENTER_PLACEHOLDER")}
                      options={casesTypes}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error.message}</div>
                    )}
                  </>
                )}
              />
            </FormField>
            {/* Action Taken - full width textarea */}
            <Col xs={12}>
              <FormGroup className="mb-3">
                <Label className="fw-bold" htmlFor="action_taken">
                  {t("ACTION_TAKEN")} <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="action_taken"
                  control={control}
                  render={({ field, fieldState: { invalid, error } }) => {
                    const displayValue = caseTypeActionsText || field.value || "";
                    return (
                      <>
                        <TextArea
                          {...field}
                          value={displayValue}
                          onChange={() => { }}
                          id="action_taken"
                          placeholder={t("ENTER_PLACEHOLDER")}
                          status={invalid ? "error" : undefined}
                          rows={4}
                          readOnly
                          style={{ borderRadius: "0.75rem" }}
                        />
                        {error && (
                          <div className="text-danger small mt-1">{error.message}</div>
                        )}
                      </>
                    );
                  }}
                />
              </FormGroup>
            </Col>
            <FormField name="case_authority_id" label={t("CASE_AUTHORITY")} required colProps={{ xs: 12, sm: 6 }} errorInside>
              <Controller
                name="case_authority_id"
                control={control}
                rules={{ required: t("FIELD_REQUIRED") }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <>
                    <AntSelect
                      {...field}
                      status={invalid ? "error" : undefined}
                      allowClear
                      placeholder={t("ENTER_PLACEHOLDER")}
                      options={caseAuthorityOptions.filter((option) => option.system === system)}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error.message}</div>
                    )}
                  </>
                )}
              />
            </FormField>
            <FormField name="card_status_id" label={t("CARD_STATUS")} colProps={{ xs: 12, sm: 6 }} errorInside>
              <Controller
                name="card_status_id"
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                  <>
                    <AntSelect
                      {...field}
                      status={invalid ? "error" : undefined}
                      allowClear
                      placeholder={t("ENTER_PLACEHOLDER")}
                      options={cardStatusOptions}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error.message}</div>
                    )}
                  </>
                )}
              />
            </FormField>
            {/* <Controller
              name="case_status_id"
              control={control}
              render={({ field, fieldState: { invalid, error } }) => (
                <FormField name={field.name} label={t("CASE_STATUSES")} colProps={{ xs: 12, sm: 6 }} errorInside>
                  <AntSelect
                    {...field}
                    id={field.name}
                    status={invalid ? "error" : undefined}
                    allowClear
                    placeholder={t("ENTER_PLACEHOLDER")}
                    options={caseStatusesOptions}
                  />
                  {error && (
                    <div className="text-danger small mt-1">{error.message}</div>
                  )}
                </FormField>
              )}
            /> */}

            <FormField name="report_authority_id" label={t("REPORT_AUTHORITY")} required colProps={{ xs: 12, sm: 6 }} errorInside>
              <Controller
                name="report_authority_id"
                control={control}
                rules={{ required: t("FIELD_REQUIRED") }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <>
                    <AntSelect
                      {...field}
                      status={invalid ? "error" : undefined}
                      allowClear
                      placeholder={t("ENTER_PLACEHOLDER")}
                      options={reportAuthorityOptions}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error.message}</div>
                    )}
                  </>
                )}
              />
            </FormField>
            {/* Report Date – value sent as "YYYY-MM-DD HH:mm:ss" */}
            <FormField name="reported_at" label={t("CASE_DATE")} required colProps={{ xs: 12, sm: 6 }} errorInside>
              <Controller
                name="reported_at"
                control={control}
                rules={{ required: t("FIELD_REQUIRED") }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <>
                    <DatePicker
                      {...field}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date) => field.onChange(date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : null)}
                      id="reported_at"
                      className="w-100 rounded-3"
                      placeholder={t("ENTER_PLACEHOLDER")}
                      format="YYYY-MM-DD HH:mm:ss"
                      showTime={{ format: "HH:mm:ss" }}
                      status={invalid ? "error" : undefined}
                      allowClear={false}
                      style={{ height: "50px" }}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error.message}</div>
                    )}
                  </>
                )}
              />
            </FormField>
            <FormField name="document_type_id" label={t("DOCUMENT_TYPES")} colProps={{ xs: 12, sm: 6 }} errorInside>
              <Controller
                name="document_type_id"
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                  <>
                    <AntSelect
                      {...field}
                      status={invalid ? "error" : undefined}
                      allowClear
                      placeholder={t("ENTER_PLACEHOLDER")}
                      options={documentTypeOptions}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error.message}</div>
                    )}
                  </>
                )}
              />
            </FormField>
            {/* Document Number */}
            <FormField name="document_number" label={t("DOCUMENT_NUMBER")} colProps={{ xs: 12, sm: 6 }} errorInside>
              <Controller
                name="document_number"
                control={control}
                render={({ field, fieldState: { invalid, error } }) => (
                  <>
                    <AntInput
                      {...field}
                      status={invalid ? "error" : undefined}
                      id="document_number"
                      placeholder={t("ENTER_PLACEHOLDER")}
                    />
                    {error && (
                      <div className="text-danger small mt-1">{error.message}</div>
                    )}
                  </>
                )}
              />
            </FormField>
            <Col {...fullWidthCol}>
              <FormGroup className="mb-3">
                <Label className="fw-bold" for="description">
                  {t("CASE_DATA")} <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="description"
                  control={control}
                  rules={{ required: t("FIELD_REQUIRED") }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <>
                      <TextArea
                        {...field}
                        id="description"
                        placeholder={t("ENTER_PLACEHOLDER")}
                        status={invalid ? "error" : undefined}
                        rows={4}
                        style={{ borderRadius: "0.75rem" }}
                      />
                      {error && (
                        <div className="text-danger small mt-1">{error.message}</div>
                      )}
                    </>
                  )}
                />
              </FormGroup>
            </Col>
          </Row>
        </Col>
      </Row>

        <Row>
          <Col xs={12} md={9} lg={9}>
            <Row className="g-2 mb-2">
            {isDeathCase && (
              <FormField name="body_location_id" label={t("BODY_LOCATION")} colProps={{ xs: 12, sm: 6 }} errorInside>
                <Controller
                  name="body_location_id"
                  control={control}
                  // rules={{ required: t("FIELD_REQUIRED") }}
                  render={({ field, fieldState: { invalid, error } }) => (
                    <>
                      <AntSelect
                        {...field}
                        status={invalid ? "error" : undefined}
                        allowClear
                        placeholder={t("ENTER_PLACEHOLDER")}
                        options={bodyLocationOptions}
                      />
                      {error && (
                        <div className="text-danger small mt-1">{error.message}</div>
                      )}
                    </>
                  )}
                />
              </FormField>
            )}
              <Col xs={12} sm={6}>
                <FormGroup className="mb-3">
                  <Label className="fw-bold" for="documents">
                    {t("ATTACHMENTS")} <span className="text-danger">*</span>
                  </Label>
                  <DocumentField control={control} healthCertificateInputRef={healthCertificateInputRef} existingDocuments={existingDocuments} reportId={reportId} />
                </FormGroup>
              </Col>
            </Row>
          </Col>
        </Row>
      <FormField name="resolved_at" colProps={{ ...threeCol, className: "d-flex align-items-center" }}>
        <Controller
          name="resolved_at"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Label check className="d-flex align-items-center gap-2 mb-0">
              <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
              {t("CASE_COMPLETED")}
            </Label>
          )}
        />
      </FormField>
      {watch?.("resolved_at") && (
        <FormField name="resolve_reason" colProps={fullWidthCol}>
          <Controller
            name="resolve_reason"
            control={control}
            render={({ field }) => (
              <>
                <Label for="resolve_reason" className="fw-bold">
                  {t("RESOLUTION_REASON")}
                </Label>
                <TextArea
                  id="resolve_reason"
                  {...field}
                  placeholder={t("RESOLUTION_REASON_PLACEHOLDER")}
                  rows={3}
                  className="w-100"
                />
              </>
            )}
          />
        </FormField>
      )}
    </>
  );
}

export default CaseDetails;
